import { Editor } from 'grapesjs';
import i18n from 'i18next';

/**
 * Registra componentes customizados no GrapesJS com traits avançados
 * para imagens, botões, seções e WhatsApp.
 */
export const registerCustomComponents = (editor: Editor): void => {
  const domComponents = editor.DomComponents;

  // ============================================================
  // COMPONENTE: Imagem Avançada
  // ============================================================
  domComponents.addType('custom-image', {
    isComponent: (el: HTMLElement) => el.tagName === 'IMG',
    extend: 'image',
    model: {
      defaults: {
        traits: [
          { type: 'text', name: 'src', label: 'URL da Imagem', placeholder: 'Cole a URL da imagem aqui' },
          { type: 'text', name: 'alt', label: 'Texto Alternativo', placeholder: 'Descreva a imagem' },
          { type: 'number', name: 'data-opacity', label: 'Opacidade (%)', placeholder: '100', min: 0, max: 100, step: 5 },
          {
            type: 'select', name: 'data-object-fit', label: 'Ajuste da Imagem',
            options: [
              { id: 'cover', name: 'Cobrir' }, { id: 'contain', name: 'Conter' },
              { id: 'fill', name: 'Preencher' }, { id: 'none', name: 'Original' },
            ],
          },
          {
            type: 'select', name: 'data-object-position', label: 'Posicao',
            options: [
              { id: 'center', name: 'Centro' }, { id: 'top', name: 'Topo' },
              { id: 'bottom', name: 'Inferior' }, { id: 'left', name: 'Esquerda' },
              { id: 'right', name: 'Direita' },
            ],
          },
          {
            type: 'select', name: 'data-border-style', label: 'Formato da Borda',
            options: [
              { id: 'none', name: 'Sem borda' }, { id: 'rounded-sm', name: 'Leve (4px)' },
              { id: 'rounded', name: 'Arredondada (8px)' }, { id: 'rounded-lg', name: 'Muito arredondada (16px)' },
              { id: 'rounded-full', name: 'Circular (50%)' },
              { id: 'blob-1', name: 'Blob Organico 1' }, { id: 'blob-2', name: 'Blob Organico 2' },
            ],
          },
          { type: 'number', name: 'data-border-width', label: 'Espessura Borda (px)', placeholder: '0', min: 0, max: 20 },
          { type: 'color', name: 'data-border-color', label: 'Cor da Borda' },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleAttrChange);
      },
      handleAttrChange(this: any) {
        const attrs = this.getAttributes();
        const style: Record<string, string> = { ...this.getStyle() };

        if (attrs['data-opacity'] !== undefined && attrs['data-opacity'] !== '') {
          style.opacity = String(Number(attrs['data-opacity']) / 100);
        }
        if (attrs['data-object-fit']) style['object-fit'] = attrs['data-object-fit'];
        if (attrs['data-object-position']) style['object-position'] = attrs['data-object-position'];

        const borderStyle = attrs['data-border-style'];
        if (borderStyle) {
          const radiusMap: Record<string, string> = {
            'none': '0', 'rounded-sm': '4px', 'rounded': '8px', 'rounded-lg': '16px',
            'rounded-full': '50%',
            'blob-1': '30% 70% 70% 30% / 30% 30% 70% 70%',
            'blob-2': '60% 40% 30% 70% / 60% 30% 70% 40%',
          };
          style['border-radius'] = radiusMap[borderStyle] || '0';
        }

        if (attrs['data-border-width'] && Number(attrs['data-border-width']) > 0) {
          style['border-width'] = `${attrs['data-border-width']}px`;
          style['border-style'] = 'solid';
          style['border-color'] = attrs['data-border-color'] || '#e5e7eb';
        }

        this.setStyle(style);
      },
    },
  });

  // ============================================================
  // COMPONENTE: Botão com Ações Configuráveis
  // ============================================================
  // Helper: collect all sections with IDs from the editor wrapper
  const collectSections = (wrapper: any): { id: string; name: string }[] => {
    const sections: { id: string; name: string }[] = [];
    const traverse = (comp: any) => {
      const tag = comp.get?.('tagName')?.toLowerCase?.() || '';
      const attrs = comp.getAttributes?.() || {};
      const compType = attrs['data-component-type'] || '';
      if ((tag === 'section' || compType === 'gradient-section') && attrs.id) {
        const desc = attrs['data-section-description'] || '';
        sections.push({ id: attrs.id, name: desc ? `${attrs.id} - ${desc}` : attrs.id });
      }
      const children = comp.components?.() || [];
      children.forEach((child: any) => traverse(child));
    };
    const children = wrapper.components?.() || [];
    children.forEach((child: any) => traverse(child));
    return sections;
  };

  // Build the button traits array based on the current action
  const buildButtonTraits = (action: string, sectionOptions: { id: string; name: string }[]) => {
    const baseTraits: any[] = [
      { type: 'text', name: 'data-btn-text', label: 'Texto do Botao', placeholder: 'Clique Aqui' },
      {
        type: 'select', name: 'data-btn-action', label: 'Acao do Botao',
        options: [
          { id: 'link', name: 'Abrir Link' }, { id: 'section', name: 'Ir para Secao' },
          { id: 'whatsapp', name: 'Abrir WhatsApp' }, { id: 'phone', name: 'Ligar' },
          { id: 'email', name: 'Enviar Email' }, { id: 'submit', name: 'Enviar Formulario' },
          { id: 'scroll-top', name: 'Voltar ao Topo' }, { id: 'download', name: 'Download' },
        ],
      },
    ];

    if (action === 'section') {
      baseTraits.push({
        type: 'select', name: 'href', label: 'Secao de Destino',
        options: [
          { id: '', name: 'Selecione uma secao...' },
          ...sectionOptions.map(s => ({ id: `#${s.id}`, name: s.name })),
        ],
      });
    } else {
      baseTraits.push(
        { type: 'text', name: 'href', label: 'URL / Destino', placeholder: 'https://... ou #secao-id' }
      );
    }

    baseTraits.push(
      { type: 'text', name: 'data-whatsapp-number', label: 'Numero WhatsApp', placeholder: '5511999999999' },
      { type: 'text', name: 'data-whatsapp-message', label: 'Mensagem WhatsApp', placeholder: 'Ola!' },
      {
        type: 'select', name: 'target', label: 'Abrir em',
        options: [{ id: '_self', name: 'Mesma aba' }, { id: '_blank', name: 'Nova aba' }],
      },
      { type: 'text', name: 'data-gradient-from', label: 'Cor Gradiente (De)', placeholder: '#6366f1' },
      { type: 'text', name: 'data-gradient-to', label: 'Cor Gradiente (Para)', placeholder: '#8b5cf6' },
      { type: 'number', name: 'data-gradient-from-pct', label: 'Gradiente De (%)', placeholder: '0', min: 0, max: 100 },
      { type: 'number', name: 'data-gradient-to-pct', label: 'Gradiente Para (%)', placeholder: '100', min: 0, max: 100 },
      { type: 'number', name: 'data-gradient-angle', label: 'Angulo Gradiente', placeholder: '135', min: 0, max: 360 },
    );

    return baseTraits;
  };

  domComponents.addType('custom-button', {
    isComponent: (el: HTMLElement) => {
      return (el.tagName === 'A' && el.getAttribute('data-component-type') === 'button') ||
        (el.tagName === 'A' && el.classList?.contains('gjs-custom-btn')) ||
        (el.tagName === 'BUTTON' && el.getAttribute('data-component-type') === 'button');
    },
    model: {
      defaults: {
        traits: [
          { type: 'text', name: 'data-btn-text', label: 'Texto do Botao', placeholder: 'Clique Aqui' },
          {
            type: 'select', name: 'data-btn-action', label: 'Acao do Botao',
            options: [
              { id: 'link', name: 'Abrir Link' }, { id: 'section', name: 'Ir para Secao' },
              { id: 'whatsapp', name: 'Abrir WhatsApp' }, { id: 'phone', name: 'Ligar' },
              { id: 'email', name: 'Enviar Email' }, { id: 'submit', name: 'Enviar Formulario' },
              { id: 'scroll-top', name: 'Voltar ao Topo' }, { id: 'download', name: 'Download' },
            ],
          },
          { type: 'text', name: 'href', label: 'URL / Destino', placeholder: 'https://... ou #secao-id' },
          { type: 'text', name: 'data-whatsapp-number', label: 'Numero WhatsApp', placeholder: '5511999999999' },
          { type: 'text', name: 'data-whatsapp-message', label: 'Mensagem WhatsApp', placeholder: 'Ola!' },
          {
            type: 'select', name: 'target', label: 'Abrir em',
            options: [{ id: '_self', name: 'Mesma aba' }, { id: '_blank', name: 'Nova aba' }],
          },
          { type: 'text', name: 'data-gradient-from', label: 'Cor Gradiente (De)', placeholder: '#6366f1' },
          { type: 'text', name: 'data-gradient-to', label: 'Cor Gradiente (Para)', placeholder: '#8b5cf6' },
          { type: 'number', name: 'data-gradient-from-pct', label: 'Gradiente De (%)', placeholder: '0', min: 0, max: 100 },
          { type: 'number', name: 'data-gradient-to-pct', label: 'Gradiente Para (%)', placeholder: '100', min: 0, max: 100 },
          { type: 'number', name: 'data-gradient-angle', label: 'Angulo Gradiente', placeholder: '135', min: 0, max: 360 },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleBtnAttrChange);
        // Set up correct traits on init based on current action
        this.refreshBtnTraits();
      },
      refreshBtnTraits(this: any) {
        const attrs = this.getAttributes();
        const action = attrs['data-btn-action'] || 'link';
        const lastTraitAction = (this as any).__lastTraitAction;

        // Only rebuild traits if action actually changed
        if (lastTraitAction === action) return;
        (this as any).__lastTraitAction = action;

        let sections: { id: string; name: string }[] = [];
        if (action === 'section') {
          try {
            const em = this.em;
            const editorInstance = em?.get?.('Editor') || em;
            const wrapper = editorInstance?.getWrapper?.();
            if (wrapper) {
              sections = collectSections(wrapper);
            }
          } catch (_e) {
            // Fallback: no sections found
          }
        }

        const newTraits = buildButtonTraits(action, sections);

        // Prevent infinite loop: temporarily remove listener
        this.off('change:attributes', this.handleBtnAttrChange);
        this.set('traits', newTraits);
        this.on('change:attributes', this.handleBtnAttrChange);
      },
      handleBtnAttrChange(this: any) {
        const attrs = this.getAttributes();
        const action = attrs['data-btn-action'];

        // Refresh traits when action changes (swap href between text/select)
        this.refreshBtnTraits();

        if (action === 'whatsapp') {
          const number = (attrs['data-whatsapp-number'] || '').replace(/\D/g, '');
          const msg = encodeURIComponent(attrs['data-whatsapp-message'] || '');
          if (number) {
            this.addAttributes({ href: `https://wa.me/${number}${msg ? '?text=' + msg : ''}`, target: '_blank' });
          }
        } else if (action === 'phone') {
          const href = attrs.href || '';
          if (href && !href.startsWith('tel:')) this.addAttributes({ href: `tel:${href.replace(/\D/g, '')}` });
        } else if (action === 'email') {
          const href = attrs.href || '';
          if (href && !href.startsWith('mailto:')) this.addAttributes({ href: `mailto:${href}` });
        } else if (action === 'section') {
          const href = attrs.href || '';
          if (href && !href.startsWith('#')) this.addAttributes({ href: `#${href}` });
        }

        const gradFrom = attrs['data-gradient-from'];
        const gradTo = attrs['data-gradient-to'];
        if (gradFrom && gradTo) {
          const angle = attrs['data-gradient-angle'] || '135';
          const fromPct = attrs['data-gradient-from-pct'] || '0';
          const toPct = attrs['data-gradient-to-pct'] || '100';
          const style = this.getStyle();
          style.background = `linear-gradient(${angle}deg, ${gradFrom} ${fromPct}%, ${gradTo} ${toPct}%)`;
          this.setStyle(style);
        }
      },
    },
  });

  // ============================================================
  // COMPONENTE: Seção com Gradiente Configurável
  // ============================================================
  domComponents.addType('custom-section', {
    isComponent: (el: HTMLElement) => {
      return el.tagName === 'SECTION' && el.getAttribute('data-component-type') === 'gradient-section';
    },
    model: {
      defaults: {
        traits: [
          { type: 'text', name: 'data-section-description', label: 'Descricao da Secao', placeholder: 'Descreva o proposito' },
          { type: 'text', name: 'data-bg-gradient-from', label: 'Gradiente Fundo (De)', placeholder: '#667eea' },
          { type: 'text', name: 'data-bg-gradient-to', label: 'Gradiente Fundo (Para)', placeholder: '#764ba2' },
          { type: 'number', name: 'data-bg-gradient-from-pct', label: 'De (%)', placeholder: '0', min: 0, max: 100 },
          { type: 'number', name: 'data-bg-gradient-to-pct', label: 'Para (%)', placeholder: '100', min: 0, max: 100 },
          { type: 'number', name: 'data-bg-gradient-angle', label: 'Angulo', placeholder: '135', min: 0, max: 360 },
          { type: 'text', name: 'id', label: 'ID da Secao', placeholder: 'secao-hero' },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleSectionAttrChange);
      },
      handleSectionAttrChange(this: any) {
        const attrs = this.getAttributes();
        const gradFrom = attrs['data-bg-gradient-from'];
        const gradTo = attrs['data-bg-gradient-to'];
        if (gradFrom && gradTo) {
          const angle = attrs['data-bg-gradient-angle'] || '135';
          const fromPct = attrs['data-bg-gradient-from-pct'] || '0';
          const toPct = attrs['data-bg-gradient-to-pct'] || '100';
          const style = this.getStyle();
          style.background = `linear-gradient(${angle}deg, ${gradFrom} ${fromPct}%, ${gradTo} ${toPct}%)`;
          this.setStyle(style);
        }
      },
    },
  });

  // ============================================================
  // COMPONENTE: Texto com Gradiente
  // ============================================================
  domComponents.addType('gradient-text', {
    isComponent: (el: HTMLElement) => el.getAttribute('data-component-type') === 'gradient-text',
    model: {
      defaults: {
        traits: [
          { type: 'text', name: 'data-text-gradient-from', label: 'Cor Gradiente (De)', placeholder: '#667eea' },
          { type: 'text', name: 'data-text-gradient-to', label: 'Cor Gradiente (Para)', placeholder: '#764ba2' },
          { type: 'number', name: 'data-text-gradient-angle', label: 'Angulo', placeholder: '135', min: 0, max: 360 },
          { type: 'number', name: 'data-text-gradient-from-pct', label: 'De (%)', placeholder: '0', min: 0, max: 100 },
          { type: 'number', name: 'data-text-gradient-to-pct', label: 'Para (%)', placeholder: '100', min: 0, max: 100 },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleGradientTextChange);
      },
      handleGradientTextChange(this: any) {
        const attrs = this.getAttributes();
        const from = attrs['data-text-gradient-from'];
        const to = attrs['data-text-gradient-to'];
        if (from && to) {
          const angle = attrs['data-text-gradient-angle'] || '135';
          const fromPct = attrs['data-text-gradient-from-pct'] || '0';
          const toPct = attrs['data-text-gradient-to-pct'] || '100';
          const style = this.getStyle();
          style.background = `linear-gradient(${angle}deg, ${from} ${fromPct}%, ${to} ${toPct}%)`;
          style['-webkit-background-clip'] = 'text';
          style['-webkit-text-fill-color'] = 'transparent';
          style['background-clip'] = 'text';
          this.setStyle(style);
        }
      },
    },
  });

  // ============================================================
  // COMPONENTE: WhatsApp Flutuante
  // ============================================================
  domComponents.addType('whatsapp-float', {
    isComponent: (el: HTMLElement) => el.getAttribute('data-component-type') === 'whatsapp-float',
    model: {
      defaults: {
        draggable: true,
        droppable: false,
        traits: [
          { type: 'text', name: 'data-wpp-number', label: 'Numero WhatsApp', placeholder: '5511999999999' },
          { type: 'text', name: 'data-wpp-message', label: 'Mensagem Padrao', placeholder: 'Ola!' },
          {
            type: 'select', name: 'data-wpp-position', label: 'Posicao',
            options: [
              { id: 'bottom-right', name: 'Inferior Direita' }, { id: 'bottom-left', name: 'Inferior Esquerda' },
              { id: 'top-right', name: 'Superior Direita' }, { id: 'top-left', name: 'Superior Esquerda' },
            ],
          },
          {
            type: 'select', name: 'data-wpp-size', label: 'Tamanho',
            options: [
              { id: 'small', name: 'Pequeno (48px)' }, { id: 'medium', name: 'Medio (60px)' }, { id: 'large', name: 'Grande (72px)' },
            ],
          },
          { type: 'text', name: 'data-wpp-tooltip', label: 'Texto do Tooltip', placeholder: 'Fale conosco!' },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleWppChange);
      },
      handleWppChange(this: any) {
        const attrs = this.getAttributes();
        const position = attrs['data-wpp-position'] || 'bottom-right';
        const size = attrs['data-wpp-size'] || 'medium';
        const sizeMap: Record<string, string> = { small: '48px', medium: '60px', large: '72px' };
        const style: Record<string, string> = {
          position: 'fixed', 'z-index': '9999',
          width: sizeMap[size] || '60px', height: sizeMap[size] || '60px',
          'border-radius': '50%', background: '#25d366',
          display: 'flex', 'align-items': 'center', 'justify-content': 'center',
          cursor: 'pointer', 'box-shadow': '0 4px 15px rgba(37,211,102,0.4)',
          transition: 'transform 0.3s, box-shadow 0.3s', 'text-decoration': 'none',
        };
        if (position.includes('bottom')) style.bottom = '24px';
        if (position.includes('top')) style.top = '24px';
        if (position.includes('right')) style.right = '24px';
        if (position.includes('left')) style.left = '24px';
        this.setStyle(style);

        const number = (attrs['data-wpp-number'] || '').replace(/\D/g, '');
        const msg = encodeURIComponent(attrs['data-wpp-message'] || '');
        if (number) {
          this.addAttributes({ href: `https://wa.me/${number}${msg ? '?text=' + msg : ''}`, target: '_blank' });
        }
      },
    },
  });

  // ============================================================
  // COMPONENTE: Botão WhatsApp Arrastável
  // ============================================================
  domComponents.addType('whatsapp-button', {
    isComponent: (el: HTMLElement) => el.getAttribute('data-component-type') === 'whatsapp-button',
    model: {
      defaults: {
        draggable: true,
        droppable: false,
        traits: [
          { type: 'text', name: 'data-wpp-number', label: 'Numero WhatsApp', placeholder: '5511999999999' },
          { type: 'text', name: 'data-wpp-message', label: 'Mensagem Padrao', placeholder: 'Ola!' },
          { type: 'text', name: 'data-wpp-btn-text', label: 'Texto do Botao', placeholder: 'Fale Conosco' },
          {
            type: 'select', name: 'data-wpp-btn-style', label: 'Estilo',
            options: [
              { id: 'filled', name: 'Preenchido (verde)' }, { id: 'outline', name: 'Contorno' },
              { id: 'dark', name: 'Escuro' }, { id: 'gradient', name: 'Gradiente' },
            ],
          },
          {
            type: 'select', name: 'data-wpp-btn-size', label: 'Tamanho',
            options: [
              { id: 'sm', name: 'Pequeno' }, { id: 'md', name: 'Medio' },
              { id: 'lg', name: 'Grande' }, { id: 'full', name: 'Largura Total' },
            ],
          },
        ],
      },
      init(this: any) {
        this.on('change:attributes', this.handleWppBtnChange);
      },
      handleWppBtnChange(this: any) {
        const attrs = this.getAttributes();
        const number = (attrs['data-wpp-number'] || '').replace(/\D/g, '');
        const msg = encodeURIComponent(attrs['data-wpp-message'] || '');
        if (number) {
          this.addAttributes({ href: `https://wa.me/${number}${msg ? '?text=' + msg : ''}`, target: '_blank' });
        }

        const btnStyle = attrs['data-wpp-btn-style'] || 'filled';
        const btnSize = attrs['data-wpp-btn-size'] || 'md';
        const style: Record<string, string> = {
          display: 'inline-flex', 'align-items': 'center', gap: '10px',
          'font-weight': '600', 'text-decoration': 'none', 'border-radius': '12px',
          cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
        };
        const sizeStyles: Record<string, Record<string, string>> = {
          sm: { padding: '10px 20px', 'font-size': '0.875rem' },
          md: { padding: '14px 28px', 'font-size': '1rem' },
          lg: { padding: '18px 36px', 'font-size': '1.125rem' },
          full: { padding: '16px 28px', 'font-size': '1rem', width: '100%', 'justify-content': 'center' },
        };
        Object.assign(style, sizeStyles[btnSize] || sizeStyles.md);

        if (btnStyle === 'filled') {
          Object.assign(style, { background: '#25d366', color: 'white', border: 'none', 'box-shadow': '0 4px 15px rgba(37,211,102,0.3)' });
        } else if (btnStyle === 'outline') {
          Object.assign(style, { background: 'transparent', color: '#25d366', border: '2px solid #25d366' });
        } else if (btnStyle === 'dark') {
          Object.assign(style, { background: '#111827', color: 'white', border: 'none' });
        } else if (btnStyle === 'gradient') {
          Object.assign(style, { background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)', color: 'white', border: 'none', 'box-shadow': '0 4px 20px rgba(37,211,102,0.35)' });
        }

        this.setStyle(style);
      },
    },
  });

  // ============================================================
  // OVERRIDE: Form component - remove action/method defaults
  // ============================================================
  domComponents.addType('form', {
    isComponent: (el: HTMLElement) => el.tagName === 'FORM',
    model: {
      defaults: {
        tagName: 'form',
        droppable: ':not(form)',
        draggable: ':not(form)',
        attributes: {},
        traits: [
          {
            type: 'text',
            name: 'data-success-message',
            label: 'Mensagem de Sucesso',
            placeholder: 'Obrigado! Entraremos em contato em breve.',
          },
        ],
      },
    },
    view: {
      events: {
        submit: (e: Event) => e.preventDefault(),
      },
    },
  });

  // The embed component is registered via its own exported function (see below).
  // This keeps it decoupled so builders that don't want the full image/button/section
  // overrides can still pick up just the embed behavior.
  registerEmbedComponent(editor);
};

/**
 * Standalone registration for the "Código HTML / Embed" component type + the
 * `open-embed-editor` command. Safe to call on its own — does NOT touch any
 * other grapesjs component type, so existing drag-and-drop behavior is preserved.
 */
export const registerEmbedComponent = (editor: Editor): void => {
  const domComponents = editor.DomComponents;

  // Guard against double registration (e.g. if a builder calls both
  // registerCustomComponents and registerEmbedComponent).
  if ((editor as any).__embedRegistered) return;
  (editor as any).__embedRegistered = true;

  const embedTitle = i18n.t('pageBuilder.blocks.customEmbed.label', 'Código HTML / Embed');
  const embedPlaceholderHint = i18n.t(
    'pageBuilder.blocks.customEmbed.placeholderHint',
    'Clique no botão abaixo (ou no ícone de edição no canto) para colar seu iframe ou HTML.'
  );
  const embedEditLabel = i18n.t('pageBuilder.blocks.customEmbed.editHtml', 'Editar código HTML');
  const embedModalTitle = i18n.t('pageBuilder.blocks.customEmbed.modalTitle', 'Editar código HTML / Embed');
  const embedModalHint = i18n.t(
    'pageBuilder.blocks.customEmbed.modalHint',
    'Cole o HTML completo do seu iframe, script ou widget. Ele vai renderizar ao vivo no preview abaixo.'
  );
  const embedPreviewLabel = i18n.t('pageBuilder.blocks.customEmbed.preview', 'Preview');
  const embedSaveLabel = i18n.t('pageBuilder.blocks.customEmbed.save', 'Salvar');
  const embedCancelLabel = i18n.t('pageBuilder.blocks.customEmbed.cancel', 'Cancelar');

  // Placeholder shown when the embed is empty — includes an inline button that
  // fires the `open-embed-editor` command so users don't have to hunt for the
  // trait panel.
  const EMBED_PLACEHOLDER_HTML = `
    <div data-embed-placeholder="1" style="padding:56px 24px;text-align:center;color:#64748b;border:2px dashed #cbd5e1;border-radius:12px;background:linear-gradient(135deg,#f8fafc 0%,#eef2ff 100%);min-height:220px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;cursor:pointer;">
      <div style="width:56px;height:56px;border-radius:14px;background:linear-gradient(135deg,#6366f1,#8b5cf6);display:flex;align-items:center;justify-content:center;box-shadow:0 8px 20px rgba(99,102,241,0.25);font-family:monospace;color:#fff;font-size:22px;font-weight:700;">&lt;/&gt;</div>
      <div style="font-weight:700;font-size:16px;color:#0f172a;">${embedTitle}</div>
      <div style="font-size:13px;max-width:420px;color:#475569;line-height:1.5;">${embedPlaceholderHint}</div>
      <div data-embed-edit-trigger="1" style="display:inline-flex;align-items:center;gap:6px;margin-top:6px;padding:10px 18px;border-radius:999px;background:#4f46e5;color:#fff;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(79,70,229,0.35);cursor:pointer;">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        ${embedEditLabel}
      </div>
    </div>
  `.trim();

  // Command: open a custom full-screen overlay with textarea + live preview.
  // We render directly to document.body (bypass editor.Modal) so the size is
  // under our control and not clipped by GrapesJS's default modal width.
  editor.Commands.add('open-embed-editor', {
    run(_ed: any, _sender: any, opts: { component?: any } = {}) {
      const comp = opts.component || editor.getSelected();
      if (!comp) return;

      // Clean up any previous instance first
      const existing = document.getElementById('gjs-embed-editor-overlay');
      if (existing) existing.remove();

      const currentHtml = String(comp.getAttributes()['data-embed-html'] || '');

      const safeSrcdoc = (html: string) => {
        return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;font-family:system-ui,sans-serif;color:#0f172a;background:#fff;}body{padding:12px;}iframe{max-width:100%;}</style></head><body>${html || '<p style="color:#94a3b8;font-size:14px;">' + embedPlaceholderHint + '</p>'}</body></html>`;
      };

      const overlay = document.createElement('div');
      overlay.id = 'gjs-embed-editor-overlay';
      overlay.style.cssText = 'position:fixed;inset:0;z-index:99999;background:rgba(15,23,42,0.75);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:20px;font-family:system-ui,-apple-system,sans-serif;';

      overlay.innerHTML = `
        <div role="dialog" aria-modal="true" style="background:#fff;border-radius:16px;box-shadow:0 25px 50px -12px rgba(0,0,0,0.5);width:100%;max-width:1100px;height:85vh;max-height:800px;display:flex;flex-direction:column;overflow:hidden;">
          <div style="display:flex;align-items:center;justify-content:space-between;padding:18px 24px;border-bottom:1px solid #e5e7eb;">
            <div style="display:flex;align-items:center;gap:12px;">
              <div style="width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#6366f1,#8b5cf6);display:flex;align-items:center;justify-content:center;font-family:monospace;color:#fff;font-size:15px;font-weight:700;">&lt;/&gt;</div>
              <h3 style="margin:0;font-size:16px;font-weight:700;color:#0f172a;">${embedModalTitle}</h3>
            </div>
            <button id="gjs-embed-editor-close" type="button" aria-label="Close" style="background:transparent;border:none;cursor:pointer;padding:8px;border-radius:8px;color:#64748b;display:flex;align-items:center;justify-content:center;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div style="flex:1;display:flex;flex-direction:column;gap:12px;padding:20px 24px;min-height:0;overflow:hidden;">
            <p style="margin:0;color:#475569;font-size:13px;line-height:1.5;">${embedModalHint}</p>
            <div style="flex:1;display:grid;grid-template-columns:1fr 1fr;gap:16px;min-height:0;">
              <div style="display:flex;flex-direction:column;gap:6px;min-height:0;">
                <label style="font-size:11px;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:0.05em;">HTML</label>
                <textarea id="gjs-embed-editor-textarea" spellcheck="false" placeholder='<iframe src="https://..." width="100%" height="900" frameborder="0"></iframe>' style="flex:1;font-family:ui-monospace,Menlo,Monaco,Consolas,monospace;font-size:13px;line-height:1.5;padding:14px;border:1px solid #e2e8f0;border-radius:10px;background:#0f172a;color:#e2e8f0;resize:none;outline:none;min-height:0;"></textarea>
              </div>
              <div style="display:flex;flex-direction:column;gap:6px;min-height:0;">
                <label style="font-size:11px;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:0.05em;">${embedPreviewLabel}</label>
                <iframe id="gjs-embed-editor-preview" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox" style="flex:1;border:1px solid #e2e8f0;border-radius:10px;background:#fff;width:100%;min-height:0;"></iframe>
              </div>
            </div>
          </div>
          <div style="display:flex;justify-content:flex-end;gap:8px;padding:16px 24px;border-top:1px solid #e5e7eb;background:#f8fafc;">
            <button id="gjs-embed-editor-cancel" type="button" style="padding:10px 18px;border-radius:8px;border:1px solid #e2e8f0;background:#fff;color:#475569;font-weight:600;font-size:14px;cursor:pointer;">${embedCancelLabel}</button>
            <button id="gjs-embed-editor-save" type="button" style="padding:10px 22px;border-radius:8px;border:none;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-weight:700;font-size:14px;cursor:pointer;box-shadow:0 4px 12px rgba(99,102,241,0.35);">${embedSaveLabel}</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);

      const textarea = overlay.querySelector('#gjs-embed-editor-textarea') as HTMLTextAreaElement;
      const previewFrame = overlay.querySelector('#gjs-embed-editor-preview') as HTMLIFrameElement;
      const saveBtn = overlay.querySelector('#gjs-embed-editor-save') as HTMLButtonElement;
      const cancelBtn = overlay.querySelector('#gjs-embed-editor-cancel') as HTMLButtonElement;
      const closeBtn = overlay.querySelector('#gjs-embed-editor-close') as HTMLButtonElement;

      textarea.value = currentHtml;
      previewFrame.srcdoc = safeSrcdoc(currentHtml);
      setTimeout(() => textarea.focus(), 50);

      const close = () => overlay.remove();
      const save = () => {
        comp.addAttributes({ 'data-embed-html': textarea.value });
        close();
      };

      let debounceTimer: any = null;
      textarea.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          previewFrame.srcdoc = safeSrcdoc(textarea.value);
        }, 300);
      });

      cancelBtn.addEventListener('click', close);
      closeBtn.addEventListener('click', close);
      saveBtn.addEventListener('click', save);
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') save();
      };
      document.addEventListener('keydown', onKey);
      const observer = new MutationObserver(() => {
        if (!document.body.contains(overlay)) {
          document.removeEventListener('keydown', onKey);
          observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true });
    },
  });

  // Helper: build the canvas preview markup for an embed.
  // Uses a srcdoc iframe so the user's HTML renders reliably inside the editor,
  // isolated from the canvas CSS and without breaking the component tree.
  const buildPreviewHtml = (rawHtml: string): string => {
    // Wrap in a minimal doc with viewport + reset so iframes and widgets behave.
    const doc = `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>html,body{margin:0;padding:0;font-family:system-ui,sans-serif;color:#0f172a;background:transparent;}body{padding:8px;}iframe,img,video{max-width:100%;}</style></head><body>${rawHtml}</body></html>`;
    const escaped = doc
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
    // Preview iframe — note the data-embed-preview flag lets toHTML() strip it on export.
    return `<iframe data-embed-preview="1" srcdoc="${escaped}" style="width:100%;min-height:560px;border:0;border-radius:12px;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.08);display:block;"></iframe>`;
  };

  domComponents.addType('custom-embed', {
    isComponent: (el: HTMLElement) => el?.getAttribute?.('data-gjs-type') === 'custom-embed',
    model: {
      defaults: {
        tagName: 'div',
        droppable: false,
        // Keep editable=true so view events fire normally. Individual children
        // get `selectable:false` applied in init() so the user can't accidentally
        // click into the preview iframe or placeholder — they always select the wrapper.
        selectable: true,
        attributes: { 'data-gjs-type': 'custom-embed', 'data-embed-html': '' },
        'custom-name': embedTitle,
        components: EMBED_PLACEHOLDER_HTML,
        traits: [
          {
            type: 'button',
            text: embedEditLabel,
            full: true,
            command: 'open-embed-editor',
            label: i18n.t('pageBuilder.blocks.customEmbed.traitHtml', 'Código HTML'),
          },
          {
            type: 'number',
            name: 'data-embed-height',
            label: i18n.t('pageBuilder.blocks.customEmbed.traitHeight', 'Altura (px)'),
            placeholder: 'ex: 900',
            min: 200,
            max: 3000,
          },
          {
            type: 'select',
            name: 'data-embed-align',
            label: i18n.t('pageBuilder.blocks.customEmbed.traitAlign', 'Alinhamento'),
            options: [
              { id: 'left', name: i18n.t('pageBuilder.blocks.customEmbed.alignLeft', 'Esquerda') },
              { id: 'center', name: i18n.t('pageBuilder.blocks.customEmbed.alignCenter', 'Centro') },
              { id: 'right', name: i18n.t('pageBuilder.blocks.customEmbed.alignRight', 'Direita') },
            ],
          },
          {
            type: 'number',
            name: 'data-embed-max-width',
            label: i18n.t('pageBuilder.blocks.customEmbed.traitMaxWidth', 'Largura máxima (px)'),
            placeholder: i18n.t('pageBuilder.blocks.customEmbed.traitMaxWidthPlaceholder', 'sem limite'),
            min: 200,
            max: 2000,
          },
        ],
      },
      init(this: any) {
        // Specific change events (faster path)
        this.on('change:attributes:data-embed-html', this.renderEmbed);
        this.on('change:attributes:data-embed-height', this.renderEmbed);
        this.on('change:attributes:data-embed-align', this.applyWrapperStyle);
        this.on('change:attributes:data-embed-max-width', this.applyWrapperStyle);
        // Safety net: grapesjs sometimes fires only the bulk event depending on
        // how the attribute was set (trait input vs programmatic). Rebind on the
        // parent event so trait changes ALWAYS re-render + restyle.
        this.on('change:attributes', () => {
          this.renderEmbed();
          this.applyWrapperStyle();
        });

        // Keep children non-selectable so clicks always bubble to the wrapper.
        const lockChildren = () => {
          (this.components?.() || []).each?.((child: any) => {
            child.set?.({ selectable: false, hoverable: false, draggable: false });
            child.get?.('components')?.each?.((grandchild: any) => {
              grandchild.set?.({ selectable: false, hoverable: false, draggable: false });
            });
          });
        };
        this.on('change:components', lockChildren);

        this.renderEmbed();
        this.applyWrapperStyle();
        lockChildren();
      },
      renderEmbed(this: any) {
        const attrs = this.getAttributes();
        const html = attrs['data-embed-html'] || '';
        const align = attrs['data-embed-align'] || 'center';
        const trimmed = String(html).trim();
        if (trimmed) {
          // Respect the user's HTML as-pasted. If a custom `data-embed-height`
          // trait is set, it overrides the iframe's fixed height so the user
          // can tune it until content fits without a scrollbar.
          const customHeight = attrs['data-embed-height'];
          let patched = trimmed;
          if (customHeight && Number(customHeight) > 0) {
            patched = patched.replace(
              /(<iframe[^>]*?)\sheight=["']?\d+["']?/gi,
              `$1 height="${Number(customHeight)}"`
            );
            if (!/height=/i.test(patched)) {
              patched = patched.replace(/<iframe\b/gi, `<iframe height="${Number(customHeight)}"`);
            }
          }
          // Flex-based alignment is bulletproof for iframes (which have
          // width="100%" + max-width in their inline style — text-align alone
          // doesn't always center them because of how browsers resolve their
          // replaced-element width).
          const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
          const wrapper = `<div data-embed-preview="1" style="display:flex;flex-direction:column;align-items:${justify};width:100%;height:auto;pointer-events:none;">${patched}</div>`;
          this.components(wrapper);
        } else {
          this.components(EMBED_PLACEHOLDER_HTML);
        }
      },
      applyWrapperStyle(this: any) {
        const attrs = this.getAttributes();
        const style: Record<string, string> = { ...this.getStyle() };
        const align = attrs['data-embed-align'] || 'center';
        const maxW = attrs['data-embed-max-width'];
        const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';

        style.display = 'flex';
        style['flex-direction'] = 'column';
        style['align-items'] = justify;
        // Clear old text-align if it was set by a previous version
        delete style['text-align'];
        if (maxW && Number(maxW) > 0) {
          style['max-width'] = `${Number(maxW)}px`;
          style['margin-left'] = align === 'left' ? '0' : 'auto';
          style['margin-right'] = align === 'right' ? '0' : 'auto';
        } else {
          delete style['max-width'];
          delete style['margin-left'];
          delete style['margin-right'];
        }
        this.setStyle(style);
      },
      // Override export: emit the user's HTML as-pasted (with optional
      // height override from the "Altura (px)" trait), wrapped in a clean div.
      toHTML(this: any, _opts: any = {}) {
        const html = this.getAttributes()['data-embed-html'];
        const attrs = this.getAttributes() || {};
        const align = attrs['data-embed-align'] || 'center';
        const maxW = attrs['data-embed-max-width'];
        const customHeight = attrs['data-embed-height'];

        const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
        const styleParts: string[] = [
          'display:flex',
          'flex-direction:column',
          `align-items:${justify}`
        ];
        if (maxW && Number(maxW) > 0) {
          styleParts.push(`max-width:${Number(maxW)}px`);
          styleParts.push(`margin-left:${align === 'left' ? '0' : 'auto'}`);
          styleParts.push(`margin-right:${align === 'right' ? '0' : 'auto'}`);
        }
        const style = styleParts.join(';');

        if (!html || !String(html).trim()) return '';

        // Apply custom height to iframes (if the user set one via the trait).
        let rawHtml = String(html);
        if (customHeight && Number(customHeight) > 0) {
          const h = Number(customHeight);
          if (/<iframe[^>]*\sheight=/i.test(rawHtml)) {
            rawHtml = rawHtml.replace(/(<iframe[^>]*?)\sheight=["']?\d+["']?/gi, `$1 height="${h}"`);
          } else {
            rawHtml = rawHtml.replace(/<iframe\b/gi, `<iframe height="${h}"`);
          }
        }

        return `<div data-gjs-type="custom-embed" data-embed-html="${rawHtml.replace(/"/g, '&quot;')}" style="${style}">${rawHtml}</div>`;
      },
    },
    view: {
      events: {
        // Double-click always opens the editor modal
        dblclick(this: any) {
          editor.runCommand('open-embed-editor', { component: this.model });
        },
        // Single-click on an empty (placeholder-showing) embed opens the modal too
        click(this: any, e: any) {
          const html = (this.model.getAttributes()['data-embed-html'] || '').trim();
          if (!html) {
            e.stopPropagation();
            editor.runCommand('open-embed-editor', { component: this.model });
          }
        },
      },
    },
  });

  // Helper: check if a grapesjs component is our custom-embed
  const isEmbedComponent = (c: any): boolean => {
    return c?.get?.('type') === 'custom-embed'
      || c?.getAttributes?.()?.['data-gjs-type'] === 'custom-embed';
  };

  // Auto-open the editor modal the moment the user drops the block on the canvas,
  // so they don't have to hunt for an Edit button.
  editor.on('component:add', (component: any) => {
    try {
      if (!isEmbedComponent(component)) return;
      const html = (component.getAttributes?.()?.['data-embed-html'] || '').trim();
      if (html) return; // Already has HTML (e.g. clone, reload) → don't interrupt
      setTimeout(() => {
        editor.select(component);
        editor.runCommand('open-embed-editor', { component });
      }, 80);
    } catch { /* noop */ }
  });

  // When the embed is selected, surface the "Configurações" (traits) panel so
  // the user immediately sees the "Editar código HTML" button on the right.
  // We dispatch a window event that the builder shell listens to.
  editor.on('component:selected', (component: any) => {
    if (!isEmbedComponent(component)) return;
    try {
      window.dispatchEvent(new CustomEvent('gjs-switch-right-panel', { detail: 'traits' }));
    } catch { /* noop */ }
  });
};
